// Task 32 — the spatial index coach's chip + script-preview dialog. Renders ONLY when the parent
// (execution-plan surface or TE filter bar) has already decided to show it — this component is
// pure presentation + its own local open/copy state and analytics, mirroring `SpatialNudgeChip`.
// The dialog's `<pre>` is read-only and its only action is Copy — NO run/execute button, by
// design (spec §15 / Task 32: "suggest, never run").

import { useEffect, useState } from 'react';
import { Check, Copy, Lightbulb } from 'lucide-react';
import { useAnalytics } from '../../../../core/analytics';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { buildSpatialIndexScript, type SpatialIndexBbox } from './spatialIndexScripts';

export type SpatialIndexHintProps = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  column: string;
  /** Human-readable evidence from `advise()` (or a generic anti-nag sentence for the TE trigger). */
  reason: string;
  /** True when `hasSpatialIndex` returned `null` (probe failed) — softens the headline, never suppresses. */
  uncertain?: boolean;
  bbox?: SpatialIndexBbox;
};

export function SpatialIndexHint({ engine, schema, table, column, reason, uncertain, bbox }: SpatialIndexHintProps) {
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const script = buildSpatialIndexScript(engine, schema, table, column, bbox);

  // Fires once per mount — the parent only renders this component while its own show condition
  // is true, so mount IS "shown" (mirrors SpatialNudgeChip's per-headline "shown" event).
  useEffect(() => {
    track('feature:spatial_index_coach', { action: 'shown' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) track('feature:spatial_index_coach', { action: 'script-viewed' });
  };

  const handleCopy = async () => {
    const ok = await writeClipboardText(script);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
    // Clipboard access can fail (permissions/headless) — the <pre> text stays selectable.
  };

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        data-testid="spatial-index-hint-chip"
        title={reason}
        className="inline-flex items-center gap-1 rounded-md border border-blue-400/60 bg-blue-50/95 px-2 py-1 text-xs font-medium text-blue-900 shadow-sm hover:bg-blue-100 dark:border-blue-500/40 dark:bg-blue-950/80 dark:text-blue-200"
      >
        <Lightbulb className="h-3.5 w-3.5 shrink-0" />
        {uncertain ? 'Consider a spatial index' : 'Add a spatial index?'}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-xl" data-testid="spatial-index-hint-dialog">
          <DialogHeader>
            <DialogTitle>Spatial index suggestion</DialogTitle>
            <DialogDescription>{reason}</DialogDescription>
          </DialogHeader>

          <pre
            data-testid="spatial-index-hint-script"
            className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 text-xs"
          >
            {script}
          </pre>

          <p className="text-[11px] text-muted-foreground">
            Preview only — review and run this yourself. Jam SQL Studio never executes index changes for you.
          </p>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={handleCopy} data-testid="spatial-index-hint-copy">
              {copied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy script'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
